/**
 * Converts Firestore Timestamp (from API) to JavaScript Date
 * Firestore Timestamps are serialized as { seconds: number, nanoseconds: number } when sent via API
 */
export const parseFirestoreDate = (timestamp) => {
  if (!timestamp) return null;
  
  // If it's already a Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // If it has toDate method (Firebase Timestamp object on client-side)
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's a Firestore Timestamp serialized from API { seconds, nanoseconds }
  // Note: Firestore Timestamps can also have _seconds and _nanoseconds (private properties)
  if (timestamp.seconds !== undefined) {
    const seconds = typeof timestamp.seconds === 'number' ? timestamp.seconds : parseInt(timestamp.seconds);
    const nanoseconds = timestamp.nanoseconds || timestamp._nanoseconds || 0;
    const nanos = typeof nanoseconds === 'number' ? nanoseconds : parseInt(nanoseconds);
    return new Date(seconds * 1000 + (nanos || 0) / 1000000);
  }
  
  // Check for _seconds (private property that might be exposed)
  if (timestamp._seconds !== undefined) {
    const seconds = typeof timestamp._seconds === 'number' ? timestamp._seconds : parseInt(timestamp._seconds);
    const nanoseconds = timestamp._nanoseconds || 0;
    const nanos = typeof nanoseconds === 'number' ? nanoseconds : parseInt(nanoseconds);
    return new Date(seconds * 1000 + (nanos || 0) / 1000000);
  }
  
  // If it's a string (ISO format)
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  
  // If it's a number (milliseconds since epoch)
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  
  // Log for debugging if we can't parse it
  console.warn('Could not parse date:', timestamp, typeof timestamp);
  return null;
};

/**
 * Formats a Firestore Timestamp (from API) to a locale date string
 */
export const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const date = parseFirestoreDate(timestamp);
  if (!date || isNaN(date.getTime())) {
    console.warn('Invalid date parsed from:', timestamp);
    return 'N/A';
  }
  
  try {
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error, 'Original value:', timestamp);
    return 'N/A';
  }
};

